var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import clsx from 'clsx';
import { IconCaretDown } from '@hawkins/icons/lib/iconCaretDown';
import { IconCaretUp } from '@hawkins/icons/lib/iconCaretUp';
import { IconClose } from '@hawkins/icons/lib/iconClose';
import { createStyles, makeStyles } from '@hawkins/styles';
import { Button } from '../../button';
import { FormGroupContext } from '../../formGroup';
var generateThemeVariables = function (theme) {
    if (theme.hawkins.theme === 'light') {
        return {
            backgroundColor: theme.hawkins.ui.backgroundColor0,
            backgroundColorDisabled: theme.hawkins.ui.backgroundColor2,
        };
    }
    return {
        backgroundColor: theme.hawkins.highlight.backgroundColorRegular,
        backgroundColorDisabled: theme.hawkins.highlight.backgroundColorRegular,
    };
};
var useDropdownTriggerStyles = makeStyles(function (theme) {
    var hawkins = theme.hawkins, hawkinsVariables = theme.hawkinsVariables;
    var themeVariables = generateThemeVariables(theme);
    return createStyles({
        root: {
            height: 40,
            padding: 12,
            fontFamily: hawkinsVariables.fontFamily.fontFamilySans,
            fontSize: hawkins.body.fontSize0,
            backgroundColor: themeVariables.backgroundColor,
            color: hawkins.textColor.textColorRegular,
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: hawkins.ui.borderColorXlight,
            borderRadius: hawkinsVariables.borderRadius.borderRadius0,
            backgroundClip: 'padding-box',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            '&:focus-within': {
                outline: 'none',
                position: 'relative',
                borderColor: 'transparent',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderColor: hawkins.focus.borderColorRegular,
                    borderWidth: hawkins.focus.borderWidthRegular,
                    borderStyle: 'solid',
                    borderRadius: hawkinsVariables.borderRadius.borderRadius0,
                    pointerEvents: 'none',
                },
            },
        },
        disabled: {
            backgroundColor: themeVariables.backgroundColorDisabled,
            color: hawkins.textColor.textColorXlight,
        },
        endAdornment: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            marginLeft: 8,
        },
        startAdornment: {
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            marginRight: 8,
        },
        startAdornmentLabelContainer: {
            display: 'flex',
            alignItems: 'center',
        },
    });
}, { name: 'Hawkins-DropdownTrigger' });
// TODO(a11y): Rewrite this to be accessible and also flexible enough to use for any arbitrary popover content
// (e.g., date picker dropdown, time picker dropdown, color picker dropdown).
// We could then expose this component to users and delete <DatePickerDropdown> and <TimePickerDropdown>.
export var DropdownTrigger = React.forwardRef(function (props, ref) {
    var _a;
    // Props
    var children = props.children, className = props.className, clearable = props.clearable, Component = props.component, disabledProp = props.disabled, hasValue = props.hasValue, onClear = props.onClear, onToggle = props.onToggle, open = props.open, startAdornment = props.startAdornment, _b = props.tabIndex, tabIndex = _b === void 0 ? 0 : _b, restProps = __rest(props, ["children", "className", "clearable", "component", "disabled", "hasValue", "onClear", "onToggle", "open", "startAdornment", "tabIndex"]);
    // Read from the form group context, if it is inside a <FormGroup/>
    var groupContext = React.useContext(FormGroupContext);
    var disabled = (groupContext === null || groupContext === void 0 ? void 0 : groupContext.disabled) || disabledProp;
    // Styles
    var classes = useDropdownTriggerStyles();
    var rootClassName = clsx(className, classes.root, (_a = {}, _a[classes.disabled] = disabled, _a));
    var handleKeyDown = function (event) {
        if (!disabled) {
            // Enter/Up/Down
            if (event.keyCode === 13 || event.keyCode === 38 || event.keyCode === 40) {
                onToggle();
            }
        }
    };
    var handleClear = React.useCallback(function (e) {
        e.stopPropagation();
        onClear();
    }, [onClear]);
    var EndAdornmentIcon = open ? IconCaretUp : IconCaretDown;
    return (_jsxs(Component, __assign({ "aria-disabled": disabled || undefined, className: rootClassName, ref: ref, tabIndex: tabIndex, onKeyDown: handleKeyDown }, restProps, { children: [_jsxs("div", __assign({ className: classes.startAdornmentLabelContainer }, { children: [_jsx("span", __assign({ className: classes.startAdornment }, { children: startAdornment })), children] })), _jsxs("span", __assign({ className: classes.endAdornment }, { children: [clearable && hasValue && (_jsx(Button, { "aria-label": "Clear", compact: true, icon: IconClose, onClick: handleClear, variant: "icon" })), _jsx(EndAdornmentIcon, { size: "small" })] }))] })));
});
DropdownTrigger.displayName = 'DropdownTrigger';
